import { RouteObject } from "react-router";
import { Navigate, useParams } from "react-router-dom";

import AuthGuard from "@/layouts/AuthGuard";

import Bid from "@/pages/Bid";
import BidDetail from "@/pages/Bid/Detail";
import BidFavorite from "@/pages/Bid/favorite";
import PastBusiness from "@/pages/PastBusiness";
import BidSpecification from "src/pages/Bid/Specification";
import BidSpecificationFavorite from "@/pages/Bid/favorite/Specification";
import BidPlan from "@/pages/Bid/Plan";
import BidPlanFavorite from "@/pages/Bid/favorite/Plan";
import ProjectManagement from "./pages/Project/Management";
import ProjectDetail from "./pages/Project/Detail";
import ProjectManagementLayout, {
  ProjectGroupsLayout,
} from "./pages/Project/layout";
import Setting from "@/pages/Setting";
import TaskManagement from "./pages/Task/Management";
import TaskDetail from "./pages/Task/Detail";
import NotFoundPage from "./pages/Errors/404";
import { RequiredParamGuard } from "./guards/RequiredParamGuard";
import { AdminGuard } from "./guards/AdminGuard";
import BidAnalytics from "./pages/Bid/Analytics";
import ProjectEstimate from "./pages/Project/Estimate";
import ProjectScenario from "./pages/Project/Scenario";
import DirectEstimatePage from "./pages/Project/Estimate/DirectEstimatePage";
import Login from "@/pages/Login";
import BidSimilar from "@/pages/Bid/Similar";
import ApprovalResponseManagement from "./pages/ApprovalResponse";
import ApprovalRequestManagement from "./pages/Approval";
import ApprovalDetailPage from "./pages/ApprovalDetail";
import DashboardBid from "@/pages/Dashboard/Bid";
import DashboardProject from "@/pages/Dashboard/Project";
import UsageDashboard from "@/pages/Usage";

export type NewRouteObject = Omit<RouteObject, "children"> & {
  title?: string;
  icon?: string;
  iconOpen?: string;
  children?: NewRouteObject[];
  link?: string;
  alt?: string;
  hidden?: boolean;
  navigate?: string;
  disabled?: boolean;
};

// 과거에 공유된 /detail/bid/:projectId 형태의 URL을 유효한 타입(original)으로 돌려보낸다
function LegacyBidDetailRedirect() {
  const { projectId } = useParams();
  return (
    <Navigate to={`/bid/domestic/detail/original/${projectId}`} replace />
  );
}

export const routes: NewRouteObject[] = [
  {
    path: "",
    element: <AuthGuard />,
    children: [
      {
        path: "dashboard",
        children: [
          { index: true, element: <Navigate to="bid" replace /> },
          { path: "bid", element: <DashboardBid /> },
          { path: "project", element: <DashboardProject /> },
        ],
      },
      {
        path: "bid",
        children: [
          {
            path: "domestic",
            children: [
              {
                path: "list",
                element: <Bid />,
              },
              {
                path: "specification",
                element: <BidSpecification />,
              },
              {
                path: "plan",
                element: <BidPlan />,
              },
              {
                path: "failure",
                element: <div>failure</div>,
              },
              {
                // 잘못된 타입 토큰으로 공유된 링크 호환용 (정적 세그먼트가 :projectType보다 우선 매칭됨)
                path: "detail/bid/:projectId",
                element: <LegacyBidDetailRedirect />,
              },
              {
                path: "detail/:projectType/:projectId",
                element: <BidDetail />,
              },
              {
                path: "similar/:projectType/:projectId",
                element: <BidSimilar />,
              },
              {
                path: "calculate",
                element: <div>투찰 가격 산출</div>,
              },
              {
                path: "detail/:projectType/:projectId/spec-analysis",
                element: <BidAnalytics />,
              },
            ],
          },
          {
            path: "favorite",
            children: [
              { path: "list", element: <BidFavorite /> },
              {
                path: "specification",
                element: <BidSpecificationFavorite />,
              },
              {
                path: "plan",
                element: <BidPlanFavorite />,
              },
            ],
          },
        ],
      },
      {
        path: "past",
        element: <PastBusiness />,
      },
      {
        path: "management",
        children: [
          {
            path: "projects",
            element: <ProjectManagementLayout />,
            children: [
              {
                path: "",
                element: <ProjectManagement />,
              },
              {
                path: ":group_id",
                element: <ProjectGroupsLayout />,
                children: [
                  {
                    path: "",
                    element: <ProjectDetail />,
                  },
                  {
                    path: "estimates/:estimate_id/original/:original_id/itemsheet/:itemsheet_id",
                    element: <DirectEstimatePage />,
                  },
                  {
                    path: "estimates",
                    element: <ProjectEstimate />,
                  },
                  {
                    path: "scenario",
                    element: <ProjectScenario />,
                  },
                ],
              },
            ],
          },
          {
            path: "tasks",
            element: <TaskManagement />,
          },
          {
            // 사용 현황이 설정으로 이동하기 전의 경로 호환용
            path: "usage",
            element: <Navigate to="/setting/usage" replace />,
          },
          {
            path: "projects/:group_id/tasks/:task_id",
            element: <TaskDetail />,
          },
        ],
      },
      {
        path: "approvals",
        children: [
          {
            path: "requests",
            element: <ApprovalRequestManagement />,
          },
          {
            path: "responses",
            element: <ApprovalResponseManagement />,
          },
          {
            path: ":approval_id",
            element: <ApprovalDetailPage />,
          },
        ],
      },
      {
        path: "setting",
        children: [
          { index: true, element: <Setting /> },
          { path: "substitute", element: <Setting /> },
          {
            path: "usage",
            element: <AdminGuard />,
            children: [
              {
                path: "",
                element: <UsageDashboard />,
              },
            ],
          },
        ],
      },
      {
        path: "/404",
        element: <NotFoundPage />,
      },
    ],
  },
  { path: "login", element: <Login /> },
];
